import { Menu, Button, rem } from "@mantine/core"
import {
  IconCalendar,
  IconBrandOffice,
  IconBrandGoogle,
  IconBrandApple,
} from "@tabler/icons-react"
import { WebCalendarProfileDto } from "../../../../../@client/Theasoft_Shared_Contracts_WebCalendar"
import { useTranslation } from "react-i18next"
import { AddToGoogleCalendarModal } from "./add-to-google-calendar-modal"
import { useCreateWebcalendarSubscription } from "@/orval/gen"
import { useInvalidate } from "@/typed-client/hooks/use-invalidate"
import { useDeviceType } from "@/-components/useDeviceType"

function openProfileAsExternal(
  calendar: WebCalendarProfileDto,
  app: "outlook" | "apple"
) {
  const isSupportingWebcals = app === "outlook"
  const url =
    isSupportingWebcals && calendar.webcalsLink && calendar.webcalsLink !== ""
      ? calendar.webcalsLink
      : calendar.webcalLink

  window.location.href = url
}

interface Props {
  profile: WebCalendarProfileDto
}

export function SubscribeMenuButton({ profile }: Props) {
  const { t } = useTranslation("app_server")
  const {
    isApple: isAppleDevice,
    isWindows: isWindowsDevice,
    isAndroid: isAndroidDevice,
  } = useDeviceType()
  const [googleCalendarModalOpen, setGoogleCalendarModalOpen] =
    React.useState(false)
  const invalidate = useInvalidate()
  const { mutateAsync: subscribeToCalendar } = useCreateWebcalendarSubscription(
    {
      mutation: {
        onSuccess: () => {
          invalidate("/api/WebCalendar/GetProfiles")
        },
      },
    }
  )
  const handleSubscribeToCalendar = async () => {
    await subscribeToCalendar({
      data: profile.webCalendarProfileId,
    })
  }
  if (!profile.isSubsribed) {
    return (
      <Button
        leftSection={<IconCalendar size={14} />}
        variant="subtle"
        size="xs"
        onClick={handleSubscribeToCalendar}
      >
        {t("Subscribe")}
      </Button>
    )
  }
  return (
    <>
      <Menu
        transitionProps={{ transition: "pop-top-right" }}
        width={220}
        withinPortal
      >
        <Menu.Target>
          <Button
            leftSection={<IconCalendar size={14} />}
            variant="subtle"
            size="xs"
          >
            {t("OpenInCalendarApp")}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {isWindowsDevice && (
            <Menu.Item
              onClick={() => openProfileAsExternal(profile, "outlook")}
              leftSection={
                <IconBrandOffice
                  style={{
                    width: rem(16),
                    height: rem(16),
                  }}
                  stroke={1.5}
                />
              }
            >
              Outlook {t("Calendar")}
            </Menu.Item>
          )}
          <Menu.Item
            onClick={() => setGoogleCalendarModalOpen(true)}
            leftSection={
              <IconBrandGoogle
                style={{
                  width: rem(16),
                  height: rem(16),
                }}
                stroke={1.5}
              />
            }
          >
            Google (Web) {t("Calendar")}
          </Menu.Item>
          {isAppleDevice && (
            <Menu.Item
              onClick={() => openProfileAsExternal(profile, "apple")}
              leftSection={
                <IconBrandApple
                  style={{
                    width: rem(16),
                    height: rem(16),
                  }}
                  stroke={1.5}
                />
              }
            >
              Apple {t("Calendar")}
            </Menu.Item>
          )}
          {isAndroidDevice && (
            <Menu.Item
              onClick={() => {
                window.location.href = profile.webcalLink
              }}
              leftSection={
                <IconCalendar
                  style={{
                    width: rem(16),
                    height: rem(16),
                  }}
                  stroke={1.5}
                />
              }
            >
              Android {t("Calendar")}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      <AddToGoogleCalendarModal
        opened={googleCalendarModalOpen}
        onClose={() => setGoogleCalendarModalOpen(false)}
        url={profile.externalLink}
      />
    </>
  )
}
